import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  TextField,
  MenuItem,
  IconButton,
  Tooltip,
  Card,
  CardContent,
  CardActions,
  Grid,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { llmModelApi, llmProviderApi } from '../../services/api';
import { PageHeader, LoadingSpinner, ErrorAlert, ConfirmDialog } from '../common';
import { LlmModelForm } from './LlmModelForm';
import type { LlmModel } from '../../types';

const getAudioInputChipState = (model: LlmModel) => {
  if (model.direct_audio_input_supported === true) {
    return { label: 'Direct audio', color: 'success' as const, variant: 'filled' as const };
  }
  if (model.transcription_supported === true) {
    return { label: 'STT', color: 'success' as const, variant: 'filled' as const };
  }
  if (model.audio_input_supported === true) {
    return { label: 'Model no audio', color: 'warning' as const, variant: 'outlined' as const };
  }
  return { label: 'Off', color: 'default' as const, variant: 'outlined' as const };
};

const getTtsChipState = (model: LlmModel) => {
  if (model.speech_synthesis_supported === true) {
    return { label: 'Supported', color: 'success' as const, variant: 'filled' as const };
  }
  if (model.audio_output_supported === true) {
    return { label: 'Model no TTS', color: 'warning' as const, variant: 'outlined' as const };
  }
  return { label: 'Off', color: 'default' as const, variant: 'outlined' as const };
};

const LlmModelList: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [providerFilter, setProviderFilter] = useState<number | ''>('');
  const [formOpen, setFormOpen] = useState(false);
  const [editingModel, setEditingModel] = useState<LlmModel | undefined>();
  const [deletingModel, setDeletingModel] = useState<LlmModel | null>(null);

  // Fetch models
  const { data, isLoading, error } = useQuery({
    queryKey: ['llm-models', providerFilter],
    queryFn: () => llmModelApi.list({ provider: providerFilter || undefined }),
  });

  // Fetch providers for filter
  const { data: providersData } = useQuery({
    queryKey: ['llm-providers'],
    queryFn: () => llmProviderApi.list({}),
  });

  if (error) {
    return <ErrorAlert error={error as Error} />;
  }

  return (
    <Box>
      <PageHeader
        title="LLM Models"
        actions={
          <Box sx={{ display: 'flex', gap: { xs: 0.5, sm: 1 }, flexWrap: 'wrap', alignItems: 'center' }}>
            <TextField
              select
              size="small"
              value={providerFilter}
              onChange={(e) => setProviderFilter(e.target.value as number | '')}
              sx={{ minWidth: { xs: 120, sm: 200 } }}
              label="Provider"
            >
              <MenuItem value="">All Providers</MenuItem>
              {providersData?.results.map((provider) => (
                <MenuItem key={provider.id} value={provider.id}>
                  {provider.name}
                </MenuItem>
              ))}
            </TextField>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                setEditingModel(undefined);
                setFormOpen(true);
              }}
              size="small"
            >
              <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Add Model</Box>
              <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>Add</Box>
            </Button>
          </Box>
        }
      />

      <LlmModelForm
        open={formOpen}
        onClose={() => {
          setFormOpen(false);
          setEditingModel(undefined);
        }}
        model={editingModel}
      />

      <ConfirmDialog
        open={!!deletingModel}
        title="Delete Model"
        message={`Are you sure you want to delete "${deletingModel?.name}"? This action cannot be undone.`}
        onConfirm={async () => {
          if (deletingModel) {
            await llmModelApi.delete(deletingModel.id);
            setDeletingModel(null);
          }
        }}
        onCancel={() => setDeletingModel(null)}
      />

      {isLoading ? (
        <LoadingSpinner message="Loading models..." />
      ) : isMobile ? (
        /* Mobile Card View */
        <Grid container spacing={{ xs: 1.5, sm: 2 }}>
          {data?.results.map((model) => (
            <Grid size={{ xs: 12, sm: 6 }} key={model.id}>
              <Card>
                <CardContent sx={{ p: { xs: 1.5, sm: 2 }, pb: 0.5 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: { xs: '0.9rem', sm: '1rem' }, wordBreak: 'break-word', pr: 1 }}>
                      {model.name}
                    </Typography>
                    <Chip
                      label={model.is_active ? 'Active' : 'Inactive'}
                      size="small"
                      color={model.is_active ? 'success' : 'default'}
                      sx={{ fontSize: '0.65rem', flexShrink: 0 }}
                    />
                  </Box>
                  
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                    {model.model}
                  </Typography>
                  
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1 }}>
                    <Chip
                      label={model.provider_name}
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: '0.65rem' }}
                    />
                    <Chip
                      label={`${model.experts_count} experts`}
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: '0.65rem' }}
                    />
                    {(() => {
                      const audioInputChip = getAudioInputChipState(model);
                      return (
                        <Chip
                          label={audioInputChip.label}
                          size="small"
                          color={audioInputChip.color}
                          variant={audioInputChip.variant}
                          sx={{ fontSize: '0.65rem' }}
                        />
                      );
                    })()}
                    {(() => {
                      const ttsChip = getTtsChipState(model);
                      return (
                        <Chip
                          label={ttsChip.label}
                          size="small"
                          color={ttsChip.color}
                          variant={ttsChip.variant}
                          sx={{ fontSize: '0.65rem' }}
                        />
                      );
                    })()}
                    <Chip
                      label={
                        model.max_context_window
                          ? `${(model.max_context_window / 1000).toFixed(0)}K ctx`
                          : 'Context unknown'
                      }
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: '0.65rem' }}
                    />
                  </Box>
                  
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Typography variant="caption" color="text.secondary">
                      In: ${(model.input_cost || 0).toFixed(2)}/1M
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Out: ${(model.output_cost || 0).toFixed(2)}/1M
                    </Typography>
                    {(model.audio_input_cost_per_hour || 0) > 0 && (
                      <Typography variant="caption" color="text.secondary">
                        Audio: ${(model.audio_input_cost_per_hour || 0).toFixed(2)}/hr
                      </Typography>
                    )}
                    {(model.audio_output_cost_per_1m_chars || 0) > 0 && (
                      <Typography variant="caption" color="text.secondary">
                        TTS: ${(model.audio_output_cost_per_1m_chars || 0).toFixed(2)}/1M chars
                      </Typography>
                    )}
                    {model.cost_threshold_tokens > 0 && (
                      <Typography variant="caption" color="warning.main">
                        Tiered &gt;{(model.cost_threshold_tokens / 1000).toFixed(0)}K
                      </Typography>
                    )}
                  </Box>
                </CardContent>
                <CardActions sx={{ justifyContent: 'flex-end', px: 1, py: 0.5 }}>
                  <IconButton
                    size="small"
                    onClick={() => {
                      setEditingModel(model);
                      setFormOpen(true);
                    }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => setDeletingModel(model)}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </CardActions>
              </Card>
            </Grid>
          ))}
          {data?.results.length === 0 && (
            <Grid size={{ xs: 12 }}>
              <Card>
                <CardContent sx={{ textAlign: 'center', py: 3 }}>
                  <Typography color="text.secondary" variant="body2">
                    No models found.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      ) : (
        /* Desktop Table View */
        <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Model ID</TableCell>
                <TableCell>Provider</TableCell>
                <TableCell>Input Cost</TableCell>
                <TableCell>Output Cost</TableCell>
                <TableCell>Context</TableCell>
                <TableCell>Audio In</TableCell>
                <TableCell>TTS</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Experts</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data?.results.map((model) => (
                <TableRow key={model.id} hover>
                  <TableCell>{model.name}</TableCell>
                  <TableCell>{model.model}</TableCell>
                  <TableCell>
                    <Chip
                      label={model.provider_name}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Tooltip title={model.cost_threshold_tokens > 0
                      ? `$${(model.input_cost || 0).toFixed(2)}/1M up to ${(model.cost_threshold_tokens / 1000).toFixed(0)}K tokens, then $${(model.input_cost_above_threshold || 0).toFixed(2)}/1M`
                      : 'Cost per 1M input tokens'
                    }>
                      <span>
                        ${(model.input_cost || 0).toFixed(2)}/1M
                        {model.cost_threshold_tokens > 0 && ` → $${(model.input_cost_above_threshold || 0).toFixed(2)}/1M`}
                      </span>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Tooltip title={model.cost_threshold_tokens > 0
                      ? `$${(model.output_cost || 0).toFixed(2)}/1M up to ${(model.cost_threshold_tokens / 1000).toFixed(0)}K tokens, then $${(model.output_cost_above_threshold || 0).toFixed(2)}/1M`
                      : 'Cost per 1M output tokens'
                    }>
                      <span>
                        ${(model.output_cost || 0).toFixed(2)}/1M
                        {model.cost_threshold_tokens > 0 && ` → $${(model.output_cost_above_threshold || 0).toFixed(2)}/1M`}
                      </span>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    {model.max_context_window
                      ? `${model.max_context_window.toLocaleString()} tokens`
                      : 'Unknown'}
                  </TableCell>
                  <TableCell>
                    {(() => {
                      const audioInputChip = getAudioInputChipState(model);
                      return (
                        <Chip
                          label={audioInputChip.label}
                          size="small"
                          color={audioInputChip.color}
                          variant={audioInputChip.variant}
                        />
                      );
                    })()}
                  </TableCell>
                  <TableCell>
                    {(() => {
                      const ttsChip = getTtsChipState(model);
                      return (
                        <Chip
                          label={ttsChip.label}
                          size="small"
                          color={ttsChip.color}
                          variant={ttsChip.variant}
                        />
                      );
                    })()}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={model.is_active ? 'Active' : 'Inactive'}
                      size="small"
                      color={model.is_active ? 'success' : 'default'}
                    />
                  </TableCell>
                  <TableCell>{model.experts_count} experts</TableCell>
                  <TableCell align="right">
                    <Tooltip title="Edit">
                      <IconButton
                        size="small"
                        onClick={() => {
                          setEditingModel(model);
                          setFormOpen(true);
                        }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => setDeletingModel(model)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {data?.results.length === 0 && (
                <TableRow>
                  <TableCell colSpan={10} align="center">
                    No models found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default LlmModelList;
